import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Phone, Loader2, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

// GET /leads?follow_up_before=<fin de hoy>&exclude_terminal_status=true —
// sin endpoint separado, decisión del Commit 13. El backend (list_leads())
// devuelve la lista ordenada por created_at, no por next_follow_up_at — se
// reordena aquí mismo en el frontend tras recibirla (array ya pequeño,
// filtrado a hoy/atrasados) en vez de tocar GET /leads para esto: no
// merece un parámetro de ordenación nuevo en el backend por un array que
// ya llega acotado. La tabla principal del dashboard sí se queda con el
// orden por created_at del backend — ahí no hay la misma urgencia.
function sortByFollowUpAscending(leads) {
  return [...leads].sort((a, b) => {
    if (!a.next_follow_up_at) return 1;
    if (!b.next_follow_up_at) return -1;
    return a.next_follow_up_at.localeCompare(b.next_follow_up_at);
  });
}

export function TodayFollowUpsWidget({ refreshKey }) {
  const { api } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFollowUps = useCallback(async () => {
    setLoading(true);
    try {
      const endOfToday = new Date();
      endOfToday.setHours(23, 59, 59, 999);
      const res = await api.get('/leads', {
        params: {
          follow_up_before: endOfToday.toISOString(),
          exclude_terminal_status: true,
          limit: 50,
        },
      });
      setLeads(sortByFollowUpAscending(res.data.leads || []));
    } catch (error) {
      console.error('Failed to fetch today follow-ups:', error);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchFollowUps();
  }, [fetchFollowUps, refreshKey]);

  const isOverdue = (nextFollowUpAt) => {
    if (!nextFollowUpAt) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(nextFollowUpAt) < today;
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-amber-400" />
        <h2 className="text-sm font-semibold text-zinc-200">
          {t('aiLeadScanner.todayFollowUps.title')}
        </h2>
        {!loading && leads.length > 0 && (
          <span className="text-xs text-zinc-500">({leads.length})</span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
        </div>
      ) : leads.length === 0 ? (
        <p className="text-sm text-zinc-500">{t('aiLeadScanner.todayFollowUps.empty')}</p>
      ) : (
        <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto">
          {leads.map((lead) => (
            <button
              key={lead.id}
              onClick={() => navigate(`/internal/ai-lead-scanner/leads/${lead.id}`)}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors text-left"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm text-zinc-200 truncate">{lead.business_name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {lead.phone && (
                    <span className="flex items-center gap-1 text-xs text-zinc-500">
                      <Phone className="w-3 h-3" /> {lead.phone}
                    </span>
                  )}
                  {isOverdue(lead.next_follow_up_at) && (
                    <span className="text-xs text-red-400 font-medium">
                      {t('aiLeadScanner.todayFollowUps.overdueLabel')}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-600 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
